import { Injectable, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { NotificationService, NotificationType, SymbolService, UserDeviceService } from '../../../../shared/services';
import { filter, first, map, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { AcceptedManners } from '../../models/orderModels.model';
import { DateHelper, isOptionSymbol } from '../../../../../tools';
import { ISymbol } from '../../../../shared/models';
import { SIDE } from '../../../../swipe';
import { AgreementStore } from '../../../../../state/agreements';
import { CustomerAgreementStateType } from '../../../../../modules/user-agreements/agreement';
import { SharedRoutes, SymbolConstants } from '@easy-lib/constants';
import { ContractStore } from '@easy-lib/state/contract/contract.store';
import { ContractConfirmStateType } from '@easy-lib/modules/user-agreements/contract';
export type CautionModel = {
	symboIsin: string;
	isMobile?: boolean;
	acceptButton?: boolean;
	showAlways?: boolean;
	side?: string;
	onAccept?: () => void;
};
@Injectable({ providedIn: 'root' })
export class CautionModalService {
	//HesamTodo need to review

	symbolService = inject(SymbolService);
	agreementStore = inject(AgreementStore);
	contractStore = inject(ContractStore);
	userDeviceService = inject(UserDeviceService);
	notificationService = inject(NotificationService);

	private _CautionModal = signal<CautionModel>(undefined);
	checkCaution = (input: CautionModel) => this._CautionModal.update(() => ({ ...input }));

	CautionModal$ = toObservable(this._CautionModal).pipe(filter(Boolean));

	CautionData$ = this.CautionModal$.pipe(
		map(caution => ({ caution, symbol: this.symbolService.symbolByIsin[caution.symboIsin] }))
	);

	Notify$ = this.CautionData$.pipe(
		switchMap(({ symbol }) => {
			if (SymbolConstants.BazarPayehMarketUnits.includes(symbol.marketUnit))
				return this.agreementStore.baseMarketAgreementStatus$.pipe(
					first(),
					map(agreementStateType => BaseMarketStateNotif[agreementStateType])
				);
			if (symbol.isCaution)
				return this.agreementStore.cautionAgreementStatus$.pipe(
					first(),
					map(agreementStateType => CautionStateNotif[agreementStateType])
				);
			if (isOptionSymbol(symbol.marketUnit))
				return this.agreementStore.basicOptionRiskAgreementStatus$.pipe(
					first(),
					withLatestFrom(this.contractStore.optionBuyContractState$),
					map(([agreementStateType, contractStateType]) => {
						const agreementState = BasicOptionStateNotif[agreementStateType];
						const contractStateState = OptionContractStateNotif[contractStateType];
						return contractStateState || agreementState;
					})
				);

			return of(undefined);
		}),
		tap(notif => {
			if (notif)
				this.notificationService.alert(
					notif.message,
					notif.type,
					false,
					this.userDeviceService.getDeviceMenuRoot() + `/${SharedRoutes.AGREEMENT_ROUTE}`
				);
		})
	);

	ShowModal$ = this.Notify$.pipe(
		filter(res => !res),
		withLatestFrom(this.CautionData$),
		map(([, { caution, symbol }]) => {
			if (caution?.showAlways) return true;

			const symbolHasCaution = (caution?.side === SIDE.BUY && !!symbol?.manner) || !!symbol?.isCaution;
			if (symbolHasCaution) return !this.isMannerAccepted(symbol);
			return false;
		})
	);
	Manner$ = this.CautionData$.pipe(
		switchMap(data =>
			this.symbolService.manners$.pipe(map(manners => manners?.find(t => t?.id === data.symbol?.manner)))
		)
	);
	Symbol = toSignal(this.CautionData$.pipe(map(res => res.symbol)));
	Manner = toSignal(this.Manner$);
	ShowModal = toSignal(
		this.ShowModal$.pipe(
			tap(isShow => {
				if (!isShow) this._CautionModal()?.onAccept?.();
			})
		)
	);
	CautionModal = toSignal(this.CautionModal$);

	isMannerAccepted(symbol: Partial<ISymbol>) {
		const acceptedManners = localStorage.getItem('AcceptedManners');
		if (acceptedManners) {
			const manners: AcceptedManners[] = JSON.parse(acceptedManners);
			return manners.some(a => a.mannerId === symbol?.manner && a.date === DateHelper.getJDate(new Date()));
		}
		return false;
	}
	onMannerAccept(): void {
		const localData = localStorage.getItem('AcceptedManners');
		let acceptedManners: AcceptedManners[] = [];
		if (localData) {
			acceptedManners = JSON.parse(localData);
			acceptedManners = acceptedManners.filter(a => a.date === DateHelper.getJDate(new Date()));
		}
		acceptedManners.push({
			date: DateHelper.getJDate(new Date()),
			mannerId: this.Symbol()?.manner
		});
		localStorage.setItem('AcceptedManners', JSON.stringify(acceptedManners));
		this._CautionModal()?.onAccept?.();
	}
}

const BaseMarketStateNotif: { [key in CustomerAgreementStateType]: { type: NotificationType; message: string } } = {
	[CustomerAgreementStateType.Unseen]: {
		type: NotificationType.warning,
		message: 'توافقنامه ریسک معاملات در بازار پایه را از قسمت توافقنامه ها تایید کنید'
	},
	[CustomerAgreementStateType.Inactive]: {
		type: NotificationType.danger,
		message:
			'شما به دلیل نپذیرفتن توافقنامه ریسک معاملات در بازار پایه امکان معامله ندارید. با پشتیبانی تماس بگیرید '
	},
	[CustomerAgreementStateType.Active]: undefined
};
const CautionStateNotif: { [key in CustomerAgreementStateType]: { type: NotificationType; message: string } } = {
	[CustomerAgreementStateType.Unseen]: {
		type: NotificationType.warning,
		message: 'توافقنامه معاملات تحت احتیاط را از قسمت توافقنامه ها تایید کنید'
	},
	[CustomerAgreementStateType.Inactive]: {
		type: NotificationType.danger,
		message: 'شما به دلیل نپذیرفتن توافقنامه معاملات تحت احتیاط امکان معامله ندارید. با پشتیبانی تماس بگیرید '
	},
	[CustomerAgreementStateType.Active]: undefined
};
const BasicOptionStateNotif: { [key in CustomerAgreementStateType]: { type: NotificationType; message: string } } = {
	[CustomerAgreementStateType.Unseen]: {
		type: NotificationType.warning,
		message: 'جهت دسترسی، توافقنامه ریسک معاملات در بازار اختیار را از قسمت توافقنامه ها تایید کنید'
	},
	[CustomerAgreementStateType.Inactive]: {
		type: NotificationType.danger,
		message: 'شما به دلیل نپذیرفتن توافقنامه ریسک معاملات در بازار اختیار، امکان معامله ندارید.'
	},
	[CustomerAgreementStateType.Active]: undefined
};
const OptionContractStateNotif: { [key in ContractConfirmStateType]: { type: NotificationType; message: string } } = {
	[ContractConfirmStateType.NotRequested]: {
		type: NotificationType.danger,
		message:
			'شما به بازار اختیار معاملات دسترسی ندارید!. برای ایجاد دسترسی ابتدا می‌بایست از قسمت قرارداد ها، قرارداد مربوطه را امضا کنید'
	},
	[ContractConfirmStateType.PendingForReview]: {
		type: NotificationType.danger,
		message: 'مشتری گرامی درخواست دسترسی شما به سرویس اختیار معاملات توسط کارشناسان ما در حال بررسی‌ می‌باشد'
	},
	[ContractConfirmStateType.Rejected]: {
		type: NotificationType.danger,
		message: 'مشتری گرامی درخواست دسترسی شما به سرویس اختیار معاملات توسط کارشناسان ما رد شده است'
	},
	[ContractConfirmStateType.Confirm]: undefined
};
